// ABOUTME: Radio group for selecting the number of full truck loads (1, 2, 3).
// Renders options from the fullLoad configuration with quantity ranges.

import {FullLoadsModeReturn} from '@hconnect/tuc-processes/fields/quantity/types'
import {ControlledRadioGroup} from '@hm/fundamental'
import {Box, Typography} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

interface FullLoadsSectionProps {
  /** Pre-narrowed hook return — must be type === 'fullLoads' */
  quantityReturn: FullLoadsModeReturn
}

/**
 * Radio selection for full truck load options.
 * Each option shows the load count and the corresponding quantity range.
 */
export const FullLoadsSection: React.FC<FullLoadsSectionProps> = ({quantityReturn}) => {
  const {t} = useTranslation()
  const {fullLoads, setFullLoads, fullLoadOptions} = quantityReturn

  const radioOptions = useMemo(
    () =>
      fullLoadOptions.map((option) => {
        const loadLabel =
          option.numberOfLoads === 1
            ? t('orderIntake.rmc.steps.quantity.fullLoad', '{{count}} full load', {
                count: option.numberOfLoads
              })
            : t('orderIntake.rmc.steps.quantity.fullLoads', '{{count}} full loads', {
                count: option.numberOfLoads
              })
        return {
          value: String(option.numberOfLoads),
          label: `${loadLabel}: ${option.minQuantity} – ${option.maxQuantity} ${option.uom}`
        }
      }),
    [fullLoadOptions, t]
  )

  const handleChange = (value: string) => {
    setFullLoads(Number(value))
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%'
      }}
      data-test-id="full-loads-section"
    >
      <ControlledRadioGroup
        name="full-loads"
        value={String(fullLoads)}
        onChange={handleChange}
        options={radioOptions}
        data-cy="full-loads-radio-group"
      />

      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          color: '#54708c'
        }}
      >
        {t(
          'orderIntake.rmc.steps.quantity.fullLoadsHint',
          "For larger orders, use the 'By quantity' tab."
        )}
      </Typography>
    </Box>
  )
}
